import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  CheckCircle,
  XCircle,
  Loader2,
  ArrowRight
} from 'lucide-react';
import axios from 'axios';
import { API_ENDPOINTS } from '@/constants/api';
import { authService } from '@/services/auth';

interface OAuthStatus {
  status: 'loading' | 'success' | 'error';
  message: string;
  error?: string;
  data?: any;
}

const DashboardOAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [oauthStatus, setOAuthStatus] = useState<OAuthStatus>({
    status: 'loading',
    message: 'Processing OAuth callback...'
  });

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const code = searchParams.get('code');
        const error = searchParams.get('error');
        const state = searchParams.get('state');

        if (error) {
          setOAuthStatus({
            status: 'error',
            message: 'OAuth authentication failed',
            error: decodeURIComponent(error)
          });
          return;
        }

        if (!code) {
          setOAuthStatus({
            status: 'error',
            message: 'No authorization code received',
            error: 'Missing authorization code from Google'
          });
          return;
        }

        // Exchange authorization code for tokens
        setOAuthStatus({
          status: 'loading',
          message: 'Exchanging authorization code for tokens...'
        });

        console.log('Sending OAuth code to backend:', {
          code: code.substring(0, 20) + '...', // Log partial code for debugging
          hasAuthToken: !!authService.getAuthToken()
        });

        const response = await axios.post(API_ENDPOINTS.ANALYTICS_OAUTH_CALLBACK, {
          code: code,
          state: state
        }, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getAuthToken()}`
          }
        });

        const data = response.data;

        setOAuthStatus({
          status: 'success',
          message: 'Google Analytics connected successfully!',
          data: data
        });

        // Auto-redirect to property selector after a delay
        setTimeout(() => {
          navigate('/analytics/properties');
        }, 3000);

      } catch (err: any) {
        console.error('OAuth callback error:', err);
        const errorMessage = err.response?.data?.message || err.message || 'Unknown error occurred';
        setOAuthStatus({
          status: 'error',
          message: 'Failed to complete OAuth authentication',
          error: errorMessage
        });
      }
    };

    handleCallback();
  }, [searchParams, navigate]);



  const handleRetry = () => {
    navigate('/');
  };

  const handleGoToProperties = () => {
    navigate('/analytics/properties');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold tracking-tight">OAuth Callback</h1>
        <p className="text-muted-foreground mt-2">
          Processing your Google Analytics authorization
        </p>
      </div>

      {/* Status Card */}
      <div className="w-full">
        <Card className="border-2 border-dashed border-muted-foreground/25">
          <CardHeader className="text-center pb-4">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              {oauthStatus.status === 'loading' && (
                <Loader2 className="h-8 w-8 text-primary animate-spin" />
              )}
              {oauthStatus.status === 'success' && (
                <CheckCircle className="h-8 w-8 text-green-500" />
              )}
              {oauthStatus.status === 'error' && (
                <XCircle className="h-8 w-8 text-red-500" />
              )}
            </div>
            <CardTitle className="text-xl">
              {oauthStatus.status === 'loading' && 'Processing Authorization...'}
              {oauthStatus.status === 'success' && 'Authorization Successful!'}
              {oauthStatus.status === 'error' && 'Authorization Failed'}
            </CardTitle>
            <CardDescription>
              {oauthStatus.message}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-6">
            
            {/* Status Details */}
            <div className="space-y-4">
              {oauthStatus.status === 'loading' && (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    Please wait while we complete the setup...
                  </p>
                  <div className="flex justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                </div>
              )}

              {oauthStatus.status === 'success' && (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <p className="text-sm font-medium text-green-800">
                        Google Analytics connected successfully
                      </p>
                    </div>
                    <p className="text-sm text-green-600 mt-1">
                      Redirecting to property selection...
                    </p>
                  </div>
                  
                  <div className="flex justify-center space-x-4">
                    <Button 
                      onClick={handleGoToProperties}
                      className="px-6"
                    >
                      <ArrowRight className="mr-2 h-4 w-4" />
                      Continue to Properties
                    </Button>
                  </div>
                </div>
              )}

              {oauthStatus.status === 'error' && (
                <div className="space-y-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2">
                      <XCircle className="h-5 w-5 text-red-500" />
                      <p className="text-sm font-medium text-red-800">
                        Connection failed
                      </p>
                    </div>
                    {oauthStatus.error && (
                      <p className="text-sm text-red-600 mt-1">
                        {oauthStatus.error}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex justify-center space-x-4">
                    <Button 
                      variant="outline"
                      onClick={handleRetry}
                    >
                      Try Again
                    </Button>
                    <Button 
                      onClick={() => navigate('/')}
                    >
                      Back to Dashboard
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Connection Info */}
            {oauthStatus.status === 'success' && (
              <div className="bg-muted/30 rounded-lg p-4">
                <h4 className="font-medium text-sm mb-2">Connection Details</h4>
                <ul className="text-xs space-y-1 text-muted-foreground">
                  <li>• Read-only access to Google Analytics</li>
                  <li>• One connection per organization</li>
                  <li>• Secure OAuth 2.0 authentication</li>
                  <li>• Next step: Choose your website</li>
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardOAuthCallback;
